import type {IEventChannel, ApiOpts} from "@esWebAPI/es/model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type { Feature } from "../model/Feature";

export default class ExportModule {

    private eventChannel: IEventChannel;
    private readonly rqBroker: AjaxRequestBroker;
    private readonly resourceName: string;
    private readonly cache: CacheModule

    public constructor (eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.eventChannel.addListener("onDestroy", () => {
            this.eventChannel = null; // prevent memory leaks when the map widget is destroyed
        });
    }

    /**
     * Export given features as CSV. Keep the feature order.
     * @param geoMapId
     * @param features The features to be exported
     * @param options Jquery.fileDownload options
     * @returns $.Deferred.promise
     */
    public exportFeatures(geoMapId: number, features: Feature[], options) {

        const data = {
            geoMapId: geoMapId,
            featureUniqIds: features.map(function(f) { return { featureId: f.id, layerViewId: f.layerViewId }; })
        };

        options = $.extend(options || {}, {
            httpMethod: 'POST',
            data: {
                query: JSON.stringify(data)
            }
        });

        if ($("meta[name='_csrf_parameter']").length) { // && $("meta[name='_csrf']").length
            const csrfParamName = $("meta[name='_csrf_parameter']").attr("content");
            const csrfToken = $("meta[name='_csrf']").attr("content");

            options.data[csrfParamName] = encodeURIComponent(csrfToken);
        }

        // @ts-ignore
        return $.fileDownload(es.restUrl + '/export', options);
    };

    public exportScheduleServices(geoMapId: number, services: any[], options) {
        const featureUniqIds = services.map(s => { return {featureId: s.featureId, layerViewId: s.layerViewId}; });
        const arrivalTimes = services.map(s => esApp.map.schedule.util.getStartTime(s) * 1000);

        const data = {
            $type: "ScheduleExportRequest",
            geoMapId: geoMapId,
            featureUniqIds: featureUniqIds,
            arrivalTimes: arrivalTimes,
            timezone: es.Intl && es.Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
        };

        options = $.extend(options || {}, {
            httpMethod: 'POST',
            data: {
                query: JSON.stringify(data)
            }
        });

        if ($("meta[name='_csrf_parameter']").length) { // && $("meta[name='_csrf']").length
            const csrfParamName = $("meta[name='_csrf_parameter']").attr("content");
            const csrfToken = $("meta[name='_csrf']").attr("content");
            options.data[csrfParamName] = encodeURIComponent(csrfToken);
        }

        // @ts-ignore
        return $.fileDownload(es.restUrl + '/export/schedule', options).fail(function() {
            es.error.handleServerError($i18n("_Error_Export_Schedule_CSV"));
        });
    };

    /**
     * Export Day Trip and Multi Day Trip Schedule to email
     * @param geoMapId
     * @param username
     * @param mapType - either Google, Waze or Apple
     * @param stopColumnEnabled
     * @param dateColumnEnabled
     * @param userSelectedColumns - columns per layer view that will be exported
     * @param opts
     */
    public exportScheduleToEmail(geoMapId: number,
                                 username: string,
                                 mapType: 'GOOGLE' | 'WAZE' | 'APPLE',
                                 stopColumnEnabled: boolean,
                                 dateColumnEnabled: boolean,
                                 userSelectedColumns: Record<number, string[]>,
                                 opts?: ApiOpts){

        const url = es.api.getURL(this.resourceName) + "/schedule/email/";
        const postData =  {
            geoMapId: geoMapId,
            username: username,
            mapType: mapType,
            stopColumnEnabled: stopColumnEnabled,
            dateColumnEnabled: dateColumnEnabled,
            timezone: es.Intl && es.Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            columns: userSelectedColumns
        };
        return this.rqBroker.doPost({
            url: url,
            data: postData,
            opts: opts
        });
    }

    public mapBookGenerate(geoMapId: number, opts?: ApiOpts){
        const url = es.api.getURL(this.resourceName) + "/mapbook/generate/" + geoMapId;
        return this.rqBroker.doPost({'url': url, 'opts': opts});
    }

    /**
     * note: the corresponding upload for this is done in a headless browser, see mapbook-generator.js
     * todo: if we need to support >1 downloads per map we'll need some other param here (e.g. timestamp)
     */
    public mapBookDownload(geoMapId: number, uniqueCode: string, opts?: ApiOpts){
        const url = es.api.getURL(this.resourceName) + "/mapbook/download/" + geoMapId + "/" + uniqueCode;
        //return this.rqBroker.doGet({'url': url, 'opts': opts});

        // @ts-ignore
        opts = $.extend(opts || {}, {
            httpMethod: 'POST',
            data: {}
        });

        if ($("meta[name='_csrf_parameter']").length) {
            const csrfParamName = $("meta[name='_csrf_parameter']").attr("content");
            const csrfToken = $("meta[name='_csrf']").attr("content");

            opts.data[csrfParamName] = encodeURIComponent(csrfToken);
        }

        // @ts-ignore
        return $.fileDownload(url, opts);
    }
}
